import { useEffect } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { cn } from '@/lib/cn';

export interface ScoreRingProps {
  /** 0..100 */
  score: number;
  size?: number;
  /** Optional inline label rendered below the score. */
  label?: string;
  className?: string;
}

const RADIUS = 56;
const STROKE = 8;

export function ScoreRing({
  score,
  size = 140,
  label,
  className,
}: ScoreRingProps): JSX.Element {
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const circumference = 2 * Math.PI * RADIUS;

  const progress = useMotionValue(0);
  const dashOffset = useTransform(progress, (v) => circumference * (1 - v / 100));
  const display = useTransform(progress, (v) => Math.round(v).toString());

  useEffect(() => {
    const controls = animate(progress, clamped, {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
    });
    return () => controls.stop();
  }, [clamped, progress]);

  const view = RADIUS * 2 + STROKE * 2;

  return (
    <div className={cn('flex flex-col items-center gap-1', className)}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          viewBox={`0 0 ${view} ${view}`}
          width={size}
          height={size}
          className="-rotate-90"
        >
          <defs>
            <linearGradient id="score-ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" />
              <stop offset="100%" stopColor="hsl(var(--accent, var(--primary)))" />
            </linearGradient>
          </defs>
          <circle
            cx={view / 2}
            cy={view / 2}
            r={RADIUS}
            fill="none"
            stroke="hsl(var(--muted))"
            strokeWidth={STROKE}
            opacity={0.4}
          />
          <motion.circle
            cx={view / 2}
            cy={view / 2}
            r={RADIUS}
            fill="none"
            stroke="url(#score-ring-grad)"
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={circumference}
            style={{ strokeDashoffset: dashOffset }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span className="text-3xl font-semibold tabular-nums">
            {display}
          </motion.span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            score
          </span>
        </div>
      </div>
      {label ? (
        <span className="text-xs text-muted-foreground">{label}</span>
      ) : null}
    </div>
  );
}
